import React, { useState, useEffect } from 'react';
import {
  Typography,
  Row,
  Col,
  Button,
  Card,
  Space,
  Divider,
  Modal,
  Switch,
  message,
  Tag,
  Checkbox,
  Input,
  Tooltip,
  Collapse,
  Breadcrumb,
} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  FireOutlined,
  RightOutlined,
  InfoCircleOutlined,
  CrownOutlined,
  ThunderboltOutlined,
  EditOutlined,
} from '@ant-design/icons';

const { Panel } = Collapse;
import { useNavigate, Link } from 'react-router-dom';
import { getUserProfile } from '../auth';
import { Helmet } from 'react-helmet';
import { api, getCurrentSubscription, trialApi } from '../api';
import { isTestPublic } from '../utils/env';
import { trackEvent } from '../utils/ga4';
import moment from 'moment-timezone';
import ChannelTalkService from '../services/ChannelService';
import { ClockCircleOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const PlanSettings = () => {
  const [allPlans, setAllPlans] = useState([]); // すべてのプラン（API応答そのまま）
  const [userPlan, setUserPlan] = useState(null); // ユーザーの現在のプラン
  const [isYearly, setIsYearly] = useState(false); // デフォルトを月払いに変更
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [nextRenewalDate, setNextRenewalDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [newPlanPrice, setNewPlanPrice] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [ticketChange, setTicketChange] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isCouponApplying, setIsCouponApplying] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isUserLegacyPlan, setIsUserLegacyPlan] = useState(false);
  const [trialEligible, setTrialEligible] = useState(false);
  const [trialModalVisible, setTrialModalVisible] = useState(false);
  const [trialTargetPlan, setTrialTargetPlan] = useState(null);

  // 表示用プラン
  const [standardPlans, setStandardPlans] = useState([]); // 新プラン（ライト/スタンダード/プレミアム）
  const [freePlan, setFreePlan] = useState(null); // フリープラン
  const [customPlan, setCustomPlan] = useState(null); // カスタムプラン

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      console.log('データ取得開始');
      await fetchPlans();
      await fetchUserProfile();
      await fetchCurrentSubscription();
      try {
        const eligibility = await trialApi.checkEligibility();
        setTrialEligible(eligibility.eligible);
      } catch (e) {
        console.error('Trial eligibility check failed:', e);
      }
      console.log('データ取得完了');
    };
    fetchData();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/plans/');
      console.log('APIからのプラン一覧:', response.data);

      // 静的なカスタムプラン
      const customPlanData = {
        id: 'custom',
        name: 'custom',
        display_name: 'カスタムプラン',
        type: 'custom',
        monthly_tickets: '????',
        description: {
          aio_can: [
            'リサーチ機能（全ての分析項目）',
            'タイトル・構成案の自動生成',
            '構成のドラッグ＆ドロップ編集',
            '構成スコアリング・改善提案（無制限）',
            'エビデンス付きの本文生成',
            'ブラッシュアップ（10cr/回）',
            'リサーチ結果のURL共有',
            'HTMLコピー／Googleドキュメントコピー',
            'カスタムスタイル登録（無制限）',
            'スタイル微調整',
            '独自情報の登録・活用（初期50ユニット）',
            '独自情報ストレージ（500MB）',
            'パスワード付きURL共有',
          ],
          aio_cant: [],
          seo_can: [
            'スタンダードプランと同等の全機能を利用可能',
            'クレジット数のカスタマイズ',
            '請求書払い対応',
            '伴走サポートの優遇',
            '専任のサポート担当者',
          ],
          seo_cant: [],
        },
      };

      // すべてのプランを保持
      setAllPlans(response.data);

      // 名前に基づいてレガシープランか判定する関数
      const isLegacyPlan = plan => {
        return !(
          plan.name === 'lite' ||
          plan.name === 'standard_new' ||
          plan.name === 'premium_new' ||
          plan.name === 'free_new'
        );
      };

      // 上段：新プラン（ライト/スタンダード）※プレミアムは除外
      const newPlans = response.data
        .filter(
          plan =>
            !isLegacyPlan(plan) &&
            plan.type !== 'free' &&
            plan.type !== 'custom' &&
            plan.name !== 'premium_new',
        )
        .sort((a, b) => a.priority - b.priority);

      console.log('新プラン（上段）:', newPlans);
      setStandardPlans(newPlans);

      // 下段：フリープラン
      const freePlanData = response.data.find(plan => plan.type === 'free');
      console.log('フリープラン（下段）:', freePlanData);
      setFreePlan(freePlanData);

      // カスタムプラン
      const customPlanFromAPI = response.data.find(
        plan => plan.type === 'custom',
      );
      const finalCustomPlan = customPlanFromAPI || customPlanData;
      console.log('カスタムプラン（下段）:', finalCustomPlan);
      setCustomPlan(finalCustomPlan);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      message.error('プランの取得に失敗しました');
    }
  };

  const fetchUserProfile = async () => {
    try {
      const profile = await getUserProfile();
      setUserProfile(profile);
      setUserPlan(profile.plan);

      // ユーザーがレガシープランを使用しているか確認
      // 名前に基づいてレガシープランか判定
      if (profile.plan) {
        const planName = profile.plan.name;
        const isLegacy = !(
          planName === 'lite' ||
          planName === 'standard_new' ||
          planName === 'premium_new' ||
          planName === 'free_new'
        );

        if (
          isLegacy &&
          (planName === 'standard' ||
            planName === 'pro' ||
            planName === 'premium')
        ) {
          console.log(
            'ユーザーはレガシープランを使用しています:',
            profile.plan,
          );
          setIsUserLegacyPlan(true);

          // レガシー表示用にplan.is_legacyプロパティを追加
          profile.plan.is_legacy = true;
          setUserPlan(profile.plan);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      message.error('ユーザー情報の取得に失敗しました');
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const subscription = await getCurrentSubscription();
      setCurrentSubscription(subscription);
    } catch (error) {
      console.error('Failed to fetch current subscription:', error);
      message.error('現在のサブスクリプション情報の取得に失敗しました');
    }
  };

  const handlePlanChange = async planId => {
    setIsLoading(true);
    // customプランの場合は文字列'custom'が渡されるため、特別に処理
    const plan =
      planId === 'custom' ? customPlan : allPlans.find(p => p.id === planId);

    if (!plan) {
      console.error('プランが見つかりません', planId);
      message.error('プランが見つかりません');
      setIsLoading(false);
      return;
    }

    setSelectedPlan(plan);

    // チャネルトークイベントトラッキング
    ChannelTalkService.trackEvent('plan_change_attempted', {
      from_plan: userProfile?.plan?.name || 'free',
      to_plan: plan.name,
      plan_price: plan.monthly_price,
    });

    trackEvent('view_plan_details', {
      user_id: userProfile.user_id,
      current_plan: userProfile.plan.type,
      viewed_plan: plan.type,
    });

    // カスタムプランの場合は問い合わせに誘導
    if (plan.type === 'custom') {
      setIsLoading(false);
      handleCustomPlanInquiry();
      return;
    }

    try {
      const response = await api.post('/subscriptions/calculate-plan-change', {
        new_plan_id: planId,
        is_yearly: isYearly,
      });

      setNewPlanPrice(response.data.new_plan_price);
      setPaymentAmount(response.data.payment_amount);
      setNextRenewalDate(moment(response.data.next_renewal_date));
      setTicketChange(response.data.ticket_change);
      setConfirmModalVisible(true);
    } catch (error) {
      console.error('Failed to fetch prorated amount:', error);
      message.error('プラン変更の計算に失敗しました');

      // エラーイベントトラッキング
      ChannelTalkService.trackEvent('plan_change_failed', {
        error: error.response?.data?.detail || 'Unknown error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const confirmPlanChange = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/subscriptions/change-plan', {
        new_plan_id: selectedPlan.id,
        is_yearly: isYearly,
        coupon_code: appliedCoupon ? appliedCoupon.code : null,
      });

      if (response.data.status === 'checkout') {
        window.location.href = response.data.checkout_url;
      } else if (response.data.status === 'success') {
        message.success(response.data.message);
        if (selectedPlan.type === 'free') {
          setUserProfile(prevProfile => ({
            ...prevProfile,
            plan_id: response.data.new_plan_id,
            // ticket_count は廃止（クレジット制に移行）
          }));
        } else {
          await fetchUserProfile();
        }
        trackEvent('complete_plan_change', {
          user_id: userProfile.user_id,
          from_plan: userProfile.plan.type,
          to_plan: selectedPlan.type,
          is_yearly: isYearly,
        });
        await fetchCurrentSubscription();
        setConfirmModalVisible(false);
      }
    } catch (error) {
      console.error('Failed to change plan:', error);
      message.error(
        'プラン変更に失敗しました: ' +
          (error.response?.data?.detail || error.message),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomPlanInquiry = () => {
    navigate('/support');
  };

  const handleCouponApply = async () => {
    if (!couponCode.trim()) {
      message.warning('クーポンコードを入力してください');
      return;
    }

    setIsCouponApplying(true);
    try {
      const response = await api.post('/subscriptions/validate-coupon', {
        code: couponCode,
        is_yearly: isYearly,
      });
      if (response.data.is_monthly_only && isYearly) {
        message.warning('このクーポンは月払いのみ適用可能です');
        setAppliedCoupon(null);
      } else {
        setAppliedCoupon(response.data);
        message.success('クーポンが適用されました');
        await fetchPlans();
      }
    } catch (error) {
      console.error('Coupon application error:', error);
      if (error.response && error.response.data) {
        message.error(error.response.data.detail || '無効なクーポンコードです');
      } else {
        message.error('クーポンの適用に失敗しました');
      }
      setAppliedCoupon(null);
    } finally {
      setIsCouponApplying(false);
    }
  };

  useEffect(() => {
    if (appliedCoupon && appliedCoupon.is_monthly_only && isYearly) {
      setAppliedCoupon(null);
      message.warning('月払い専用クーポンが削除されました');
    }
  }, [isYearly]);

  const handleCouponRemove = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    message.success('クーポンが削除されました');
    // プランの価格を再計算
    fetchPlans();
  };

  const calculateDiscountedPrice = price => {
    if (appliedCoupon) {
      const discountAmount = price * (appliedCoupon.discount_rate / 100);
      return price - discountAmount;
    }
    return price;
  };

  const planTags = {
    // フリープラン：薄いグレー
    free: { color: 'default', text: 'フリープラン' },

    // 新旧プラン別の設定
    // ライトプラン：青系
    lite: { color: 'blue', text: 'ライトプラン' },
    // 旧スタンダードプラン：緑系
    standard: { color: 'success', text: 'スタンダードプラン' },

    // 新スタンダードプラン：緑系
    standard_new: { color: 'success', text: 'スタンダードプラン' },
    // 旧プロプラン：青系
    pro: { color: 'blue', text: 'プロプラン' },

    // プレミアムプラン：ゴールド
    premium: { color: 'warning', text: 'プレミアムプラン' },
    premium_new: { color: 'warning', text: 'プレミアムプラン' },

    // カスタムプラン：パープル系
    custom: { color: 'purple', text: 'カスタムプラン' },
  };

  const renderPlanCard = plan => {
    // カスタムプランの特別処理
    if (!plan) return null;

    // 名前に基づいてレガシープランか判定
    const isLegacyPlan =
      plan.is_legacy ||
      (plan.name &&
        !(
          plan.name === 'lite' ||
          plan.name === 'standard_new' ||
          plan.name === 'premium_new' ||
          plan.name === 'free_new'
        ) &&
        (plan.name === 'standard' ||
          plan.name === 'pro' ||
          plan.name === 'premium'));

    const yearlyPrice = calculateDiscountedPrice(plan.yearly_price || 0);
    const monthlyPrice = calculateDiscountedPrice(plan.monthly_price || 0);
    const displayPrice = isYearly ? yearlyPrice / 12 : monthlyPrice;
    const isCurrentPlan = userPlan && userPlan.id === plan.id;
    const isCustomPlan = plan.type === 'custom';

    // 表示名の横にLegacyを追加
    const displayName = isLegacyPlan
      ? `${plan.display_name}（Legacy）`
      : plan.display_name;

    // カードを3列表示に変更（4列から3列へ）
    const colSpan = { xs: 24, sm: 12, md: 8 };

    // プランの色を取得（タグ表示用）
    const getPlanColor = () => {
      if (plan.name && planTags[plan.name]) {
        return planTags[plan.name].color;
      } else if (planTags[plan.type]) {
        return planTags[plan.type].color;
      }
      return 'default';
    };

    const isFreePlan = userPlan && userPlan.type === 'free';
    const isTrialing = currentSubscription && currentSubscription.status === 'trialing';
    const canTrial = trialEligible && isFreePlan && !isTrialing && plan.type !== 'free' && plan.type !== 'custom';

    let buttonText = 'プランを選択';
    let buttonProps = {
      type: 'primary',
      onClick: () => handlePlanChange(plan.id),
      loading: isLoading && selectedPlan && selectedPlan.id === plan.id,
      disabled: isLoading,
    };

    if (isCurrentPlan) {
      if (currentSubscription && currentSubscription.status === 'canceling') {
        const endDate = moment(currentSubscription.current_period_end).tz('Asia/Tokyo').format(
          'YYYY/MM/DD',
        );
        buttonText = `現在のプラン（${endDate}まで）`;
      } else if (isTrialing) {
        buttonText = `トライアル中`;
      } else {
        buttonText = '現在のプラン';
      }
      buttonProps = { ...buttonProps, disabled: true };
    } else if (isCustomPlan) {
      buttonText = 'お気軽にお問い合わせください';
      buttonProps.onClick = handleCustomPlanInquiry;
    } else if (canTrial) {
      buttonText = '14日間無料で試してみる';
      buttonProps.onClick = () => {
        setTrialTargetPlan(plan);
        setTrialModalVisible(true);
      };
    }

    return (
      <Col
        {...colSpan}
        key={plan.id}
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        <Card
          title={
            <>
              {isLegacyPlan && isCurrentPlan && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    marginBottom: '4px',
                  }}
                >
                  現在入っているプラン
                </div>
              )}
              <div style={{ textAlign: 'center', padding: '5px 0' }}>
                <Tag
                  color={getPlanColor()}
                  style={{ fontSize: '14px', padding: '2px 10px' }}
                >
                  {displayName}
                </Tag>
              </div>
            </>
          }
          style={{
            textAlign: 'center',
            width: '100%',
            border: '1px solid #f0f0f0',
          }}
        >
          <div
            style={{
              textAlign: 'center',
              marginBottom: '16px',
              height: '80px',
            }}
          >
            {plan.type !== 'free' && !isCustomPlan && (
              <>
                {!appliedCoupon && (
                  <>
                    <Text type="secondary" style={{ fontSize: '16px' }}>
                      ¥
                    </Text>
                    <Text strong style={{ fontSize: '24px', margin: '0 4px' }}>
                      {Math.floor(displayPrice * 1.1).toLocaleString()}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      (税込) / 月あたり
                    </Text>
                  </>
                )}
                {appliedCoupon && (
                  <>
                    <Text type="secondary" style={{ fontSize: '16px' }}>
                      ¥
                    </Text>
                    <Text
                      strong
                      style={{
                        fontSize: '24px',
                        margin: '0 4px',
                        color: '#0072ff',
                      }}
                    >
                      {Math.floor(displayPrice * 1.1).toLocaleString()}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      (税込) / 初月
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      次回更新以降: ¥
                      {Math.floor(plan.monthly_price * 1.1).toLocaleString()}{' '}
                      (税込)
                    </Text>
                  </>
                )}
                <br />
                {isYearly && (
                  <>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: '12px',
                        textDecoration: 'line-through',
                      }}
                    >
                      ¥{Math.floor(monthlyPrice * 1.1).toLocaleString()} / 月
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      年間: ¥{Math.floor(yearlyPrice * 1.1).toLocaleString()}{' '}
                      (税込)
                    </Text>
                  </>
                )}
              </>
            )}
            {plan.type === 'free' && (
              <>
                <Text type="secondary" style={{ fontSize: '16px' }}>
                  ¥
                </Text>
                <Text strong style={{ fontSize: '24px' }}>
                  0
                </Text>
              </>
            )}
            {isCustomPlan && (
              <Text strong style={{ fontSize: '24px' }}>
                ASK
              </Text>
            )}
          </div>
          <Space
            direction="vertical"
            size="middle"
            style={{ width: '100%', textAlign: 'center' }}
          >
            <Text style={{ fontSize: '12px' }}>
              <FireOutlined
                style={{
                  color: '#0072ff',
                  marginRight: '4px',
                  fontSize: '18px',
                }}
              />
              月間クレジット上限：
              <span
                style={{
                  fontWeight: 'bold',
                  fontSize: '18px',
                  color: '#0072ff',
                }}
              >
                {isCustomPlan ? 'ASK' : plan.monthly_tickets}
              </span>
            </Text>
          </Space>
          <Divider style={{ margin: '16px 0' }} />
          {!isLegacyPlan && (
            <Collapse
              defaultActiveKey={['aio']}
              ghost
              style={{
                background: 'transparent',
                border: 'none',
              }}
              expandIconPosition="end"
            >
              {/* AIOコンテンツモード（デフォルト展開） */}
              <Panel
                header={
                  <Text style={{ fontWeight: 'bold', color: '#722ed1' }}>
                    <ThunderboltOutlined style={{ marginRight: 6 }} />
                    AIOコンテンツモード
                  </Text>
                }
                key="aio"
                style={{ border: 'none' }}
              >
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  {plan.description.aio_can &&
                    plan.description.aio_can.map((data, index) => (
                      <Text key={index} style={{ fontSize: '13px' }}>
                        <CheckCircleFilled
                          style={{ color: '#722ed1', marginRight: '8px' }}
                        />
                        {data}
                      </Text>
                    ))}
                  {plan.description.aio_cant &&
                    plan.description.aio_cant.map((data, index) => (
                      <Text
                        key={index}
                        type="secondary"
                        style={{ fontSize: '13px' }}
                      >
                        <CloseCircleFilled
                          style={{ color: '#ff4d4f', marginRight: '8px' }}
                        />
                        {data}
                      </Text>
                    ))}
                </Space>
              </Panel>

              {/* SEO記事生成モード（デフォルト折りたたみ） */}
              <Panel
                header={
                  <Text style={{ fontWeight: 'bold', color: '#00d4ff' }}>
                    <EditOutlined style={{ marginRight: 6 }} />
                    【旧】SEO記事生成モード
                  </Text>
                }
                key="seo"
                style={{ border: 'none' }}
              >
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  {plan.description.seo_can &&
                    plan.description.seo_can.map((data, index) => (
                      <Text key={index} style={{ fontSize: '13px' }}>
                        <CheckCircleFilled
                          style={{ color: '#00d4ff', marginRight: '8px' }}
                        />
                        {data}
                      </Text>
                    ))}
                  {plan.description.seo_cant &&
                    plan.description.seo_cant.map((data, index) => (
                      <Text
                        key={index}
                        type="secondary"
                        style={{ fontSize: '13px' }}
                      >
                        <CloseCircleFilled
                          style={{ color: '#ff4d4f', marginRight: '8px' }}
                        />
                        {data}
                      </Text>
                    ))}
                </Space>
              </Panel>
            </Collapse>
          )}
          {isCurrentPlan ? (
            <Tooltip title="現在のプランは選択不可">
              <Button
                {...buttonProps}
                size="large"
                block
                style={{
                  marginTop: '24px',
                  backgroundColor: '#f5f5f5',
                  color: '#999',
                  cursor: 'not-allowed',
                  fontWeight: 'bold',
                  border: 'none',
                }}
              >
                {buttonText}
              </Button>
            </Tooltip>
          ) : (
            <Button
              {...buttonProps}
              size="large"
              block
              style={{
                background: canTrial
                  ? 'linear-gradient(90deg, #7c3aed 0%, #a855f7 100%)'
                  : 'linear-gradient(90deg, #0072ff 0%, #00d4ff 100%)',
                border: 'none',
                transition: 'all 0.3s',
                fontWeight: 'bold',
                marginTop: 24,
              }}
              disabled={isTestPublic || buttonProps.disabled}
              title={
                isTestPublic
                  ? 'テスト公開中のためプラン変更は無効化されています'
                  : undefined
              }
              onMouseEnter={e => {
                e.currentTarget.style.opacity = '0.7';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.opacity = '1.0';
              }}
            >
              {canTrial && <ClockCircleOutlined style={{ marginRight: 6 }} />}
              {buttonText}
              <RightOutlined
                style={{
                  marginLeft: 'auto',
                  right: '10px',
                  position: 'absolute',
                  top: '50%',
                  transform: 'translate(0, -50%)',
                  fontSize: '14px',
                }}
              />
            </Button>
          )}
        </Card>
      </Col>
    );
  };

  return (
    <>
      <Helmet>
        <title>プラン変更 | magicss</title>
        <meta name="robots" content="noindex" />
        <meta property="og:title" content={`プラン変更 | magicss`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={`${process.env.PUBLIC_URL}/img/ogp.png`}
        />
        <meta
          property="og:description"
          content={`コンテンツ生成AIツール「magicss」のプラン変更ページです。`}
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div>
        <Breadcrumb
          style={{ marginBottom: 16 }}
          items={[
            { title: <Link to="/mypage">マイページ</Link> },
            { title: 'プラン変更' },
          ]}
        />
        <Title level={1} className="gradient-text" style={{ fontSize: '24px', marginBottom: 8 }}>
          ご利用プランの変更
        </Title>
        <Text type="secondary">
          このページでプランを即時変更できます。請求書払いをご希望の法人様、料金やクレジット数のカスタマイズをご希望される方はお気軽にお問い合わせください。
        </Text>
        <Divider style={{ margin: '24px 0' }} />
        <Row gutter={[16, 8]} style={{ marginBottom: '24px' }}>
          <Col xs={24} lg={4}>
            <Switch
              checked={isYearly}
              onChange={checked => setIsYearly(checked)}
              checkedChildren="年払い"
              unCheckedChildren="月払い"
              disabled={isTestPublic}
            />
          </Col>
          <Col
            xs={24}
            lg={{ span: 12, offset: 8 }}
            style={{ textAlign: 'right' }}
          >
            <Input
              placeholder="クーポンコード"
              value={couponCode}
              onChange={e => setCouponCode(e.target.value)}
              style={{ width: '200px', marginRight: '10px' }}
              disabled={appliedCoupon !== null}
            />
            {appliedCoupon ? (
              <Button onClick={handleCouponRemove}>削除</Button>
            ) : (
              <Button onClick={handleCouponApply} loading={isCouponApplying}>
                {isCouponApplying ? '適用中' : '適用'}
              </Button>
            )}
          </Col>
        </Row>

        {/* 1. 上段：現在のプラン（レガシープラン加入中ユーザーのみ） */}
        {isUserLegacyPlan && userPlan && (
          <Row
            gutter={[16, 24]}
            justify="center"
            style={{ marginBottom: '32px' }}
          >
            {renderPlanCard(userPlan)}
          </Row>
        )}

        {/* 2. 上段：新プラン（ライト/スタンダード） + カスタムプラン */}
        {(standardPlans.length > 0 || customPlan) && (
          <Row
            gutter={[16, 24]}
            justify="center"
            style={{ marginBottom: '32px' }}
          >
            {standardPlans
              .sort((a, b) => {
                const order = { lite: 1, standard_new: 2 };
                return (order[a.name] || 99) - (order[b.name] || 99);
              })
              .map(plan => renderPlanCard(plan))}
            {customPlan && renderPlanCard(customPlan)}
          </Row>
        )}

        {/* 3. 下段：フリープラン */}
        {freePlan && (
          <Row gutter={[16, 24]} justify="center">
            {renderPlanCard(freePlan)}
          </Row>
        )}

        <Modal
          title={<Title level={3}>プラン変更の確認</Title>}
          visible={confirmModalVisible}
          onOk={confirmPlanChange}
          onCancel={() => setConfirmModalVisible(false)}
          okText="プラン変更に進む"
          cancelText="キャンセル"
          width={800}
          okButtonProps={{ disabled: !isConfirmed }}
          centered
          style={{ maxHeight: '100vh' }}
          bodyStyle={{
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
          }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card>
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={8}>
                  <Text strong>現在のプラン:</Text>
                  <br />
                  <Tag
                    color={
                      userPlan
                        ? userPlan.name && planTags[userPlan.name]
                          ? planTags[userPlan.name].color
                          : planTags[userPlan.type].color
                        : 'default'
                    }
                  >
                    {userPlan
                      ? userPlan.is_legacy
                        ? `${userPlan.display_name}（Legacy）`
                        : userPlan.display_name
                      : 'プラン未設定'}
                  </Tag>
                </Col>
                <Col xs={24} lg={8}>
                  <Text strong>新しいプラン:</Text>
                  <br />
                  <Tag
                    color={
                      selectedPlan
                        ? selectedPlan.name && planTags[selectedPlan.name]
                          ? planTags[selectedPlan.name].color
                          : planTags[selectedPlan.type].color
                        : 'default'
                    }
                  >
                    {selectedPlan ? selectedPlan.display_name : 'プラン未選択'}
                  </Tag>
                </Col>
                <Col xs={24} lg={8}>
                  <Text strong>次回プラン更新日:</Text>
                  <br />
                  <Text>
                    {nextRenewalDate && nextRenewalDate.isValid()
                      ? nextRenewalDate.tz('Asia/Tokyo').format('YYYY年MM月DD日')
                      : '未定'}
                  </Text>
                </Col>
              </Row>
            </Card>
            <Card>
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <Text strong>月間クレジット上限:</Text>
                  <br />
                  <Text>
                    {userPlan ? userPlan.monthly_tickets : 0} →{' '}
                    <Text strong style={{ color: '#0072ff' }}>
                      {selectedPlan ? selectedPlan.monthly_tickets : 0}
                    </Text>
                    {userPlan && selectedPlan && selectedPlan.monthly_tickets > userPlan.monthly_tickets && (
                      <Text type="success" style={{ marginLeft: '8px' }}>
                        (+{selectedPlan.monthly_tickets - userPlan.monthly_tickets})
                      </Text>
                    )}
                    {userPlan && selectedPlan && selectedPlan.monthly_tickets < userPlan.monthly_tickets && (
                      <Text type="danger" style={{ marginLeft: '8px' }}>
                        ({selectedPlan.monthly_tickets - userPlan.monthly_tickets})
                      </Text>
                    )}
                  </Text>
                </Col>
                <Col xs={24} lg={12}>
                  <Text strong>クレジット使用量:</Text>
                  <br />
                  <Text>
                    {userProfile ? userProfile.credit_used : 0} →{' '}
                    <Text strong style={{ color: '#52c41a' }}>
                      0
                    </Text>
                    <Text type="secondary" style={{ marginLeft: '8px' }}>
                      （リセット）
                    </Text>
                  </Text>
                </Col>
              </Row>
            </Card>
            {selectedPlan && selectedPlan.type !== 'free' && (
              <Card>
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: '100%' }}
                >
                  <Row justify="space-between">
                    <Col>
                      <Text strong>今回のお支払い:</Text>
                    </Col>
                    <Col>
                      <Text
                        strong
                        style={{ fontSize: '18px', color: '#1890ff' }}
                      >
                        ¥
                        {(
                          paymentAmount -
                          (appliedCoupon
                            ? (paymentAmount * appliedCoupon.discount_rate) /
                              100
                            : 0)
                        ).toLocaleString()}
                      </Text>
                    </Col>
                  </Row>
                  {appliedCoupon && (
                    <Text type="secondary">
                      クーポン割引: ¥
                      {(
                        (paymentAmount * appliedCoupon.discount_rate) /
                        100
                      ).toLocaleString()}
                      （{appliedCoupon.discount_rate}% 割引）
                    </Text>
                  )}
                  <Text type="secondary">
                    次回更新日からの{isYearly ? '年額' : '月額'}料金: ¥
                    {(
                      (isYearly
                        ? selectedPlan.yearly_price
                        : selectedPlan.monthly_price) * 1.1
                    ).toLocaleString()}
                    （税込）
                  </Text>
                </Space>
              </Card>
            )}
            {selectedPlan && selectedPlan.type === 'free' && (
              <Card>
                <Text>
                  フリープランに変更すると、次回更新日以降は料金が発生せず、クレジットも補充されなくなります。
                </Text>
              </Card>
            )}
          </Space>
          <Divider />
          <Paragraph>
            <InfoCircleOutlined
              style={{ marginRight: '8px', color: '#1890ff' }}
            />
            プラン変更に関する注意事項:
          </Paragraph>
          <ul>
            <li>
              <Text strong>プラン変更時、クレジット使用量は0にリセットされます</Text>
            </li>
            <li>
              毎月の上限は繰り越されません（毎月リセット）
            </li>
            <li>
              フリープランへの変更は次回更新日に反映されます
            </li>
            <li>
              有料プランへの変更は即時反映されます
            </li>
            <li>
              更新時に決済が失敗すると自動的にフリープランに移行します
            </li>
            <li>
              未使用の月間クレジットは返金対象外です
            </li>
            <li>
              ボーナスクレジットは無期限で、月間クレジット消費後に使用されます
            </li>
          </ul>
          <Divider />
          <Checkbox
            checked={isConfirmed}
            onChange={e => setIsConfirmed(e.target.checked)}
          >
            注意事項を確認しました
          </Checkbox>
        </Modal>

        {/* トライアル開始モーダル */}
        <Modal
          title={
            <div style={{ textAlign: 'center', paddingBottom: 8 }}>
              <Title level={4} style={{ margin: 0 }}>
                14日間無料トライアル
              </Title>
            </div>
          }
          open={trialModalVisible}
          onCancel={() => {
            setTrialModalVisible(false);
            setTrialTargetPlan(null);
          }}
          footer={null}
          centered
          width={560}
        >
          {trialTargetPlan && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card
                style={{ background: '#f5f0ff', border: '1px solid #d3adf7' }}
              >
                <Text strong style={{ fontSize: 16 }}>
                  {trialTargetPlan.display_name}の全機能を14日間無料で体験
                </Text>
                <br />
                <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
                  月間クレジット上限: <Text strong style={{ color: '#7c3aed' }}>{trialTargetPlan.monthly_tickets}</Text>
                </Text>
              </Card>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  トライアルに含まれる機能:
                </Text>
                <Space direction="vertical" size={4}>
                  <Text><CheckCircleFilled style={{ color: '#7c3aed', marginRight: 8 }} />選択したプランの全機能が利用可能</Text>
                  <Text><CheckCircleFilled style={{ color: '#7c3aed', marginRight: 8 }} />AIリサーチ＆コンテンツ生成が体験可能</Text>
                  <Text><CheckCircleFilled style={{ color: '#7c3aed', marginRight: 8 }} />独自情報・スタイル管理も利用可能</Text>
                </Space>
              </div>

              <Divider style={{ margin: '12px 0' }} />

              <div style={{
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: '12px 16px',
              }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                  <InfoCircleOutlined style={{ marginRight: 6, color: '#8c8c8c' }} />
                  ご注意事項:
                </Text>
                <Space direction="vertical" size={2}>
                  <Text type="secondary" style={{ fontSize: 12 }}>• トライアル開始にはクレジットカードの登録が必要です</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>• 14日間のトライアル期間中、料金は発生しません</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>• トライアル終了後は自動的に有料プランに移行します</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>• トライアル期間中にいつでもキャンセル可能です</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>• キャンセルした場合、フリープランに戻ります</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>• トライアルは一社様1回限りです</Text>
                </Space>
              </div>

              <Divider style={{ margin: '12px 0' }} />

              <Button
                type="primary"
                size="large"
                block
                loading={isLoading}
                disabled={isTestPublic}
                onClick={async () => {
                  setIsLoading(true);
                  try {
                    const result = await trialApi.startTrial(trialTargetPlan.id, isYearly);
                    if (result.status === 'checkout') {
                      window.location.href = result.checkout_url;
                    }
                  } catch (err) {
                    message.error(err.response?.data?.detail || 'トライアル開始に失敗しました');
                  } finally {
                    setIsLoading(false);
                  }
                }}
                style={{
                  background: 'linear-gradient(90deg, #7c3aed 0%, #a855f7 100%)',
                  border: 'none',
                  fontWeight: 'bold',
                  height: 48,
                  fontSize: 16,
                }}
              >
                14日間無料トライアルを開始する
              </Button>

              <Text
                type="secondary"
                style={{ textAlign: 'center', display: 'block', fontSize: 12 }}
              >
                14日間は完全無料。いつでもキャンセルできます。
              </Text>
            </Space>
          )}
        </Modal>
      </div>
    </>
  );
};

export default PlanSettings;
